import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../api/firebase/config';
import { gray80, tertiaryButtonBackgroundColor, white } from '../../../constants/colors';
import { clearPatternListCache } from '../../../hooks/usePatternList';
import MetaTitle from '../../utils/MetaTitle';
import MfaEnrollDialog from '../../uiParts/mfa/MfaEnrollDialog';
import logoImage from '../../assets/logo_color.png';

const MESSAGE =
  '貴社のセキュリティ設定により、認証アプリ（二要素認証）の設定が必要です。\n設定が完了するまで Gen Drive はご利用いただけません。';

const styles = {
  // メンテナンス画面（Maintenance.tsx）と同じ構成: 白背景・公式カラーロゴを中央配置
  container: {
    alignItems: 'center',
    backgroundColor: white,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh',
    px: '24px',
    py: '48px',
    width: '100%',
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '560px',
    textAlign: 'center',
    width: '100%',
  },
  logo: {
    height: 'auto',
    width: '300px',
    '@media screen and (max-width:600px)': {
      width: '240px',
    },
  },
  message: {
    color: gray80,
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: 1.8,
    mt: '40px',
    whiteSpace: 'pre-wrap',
    '@media screen and (max-width:600px)': {
      fontSize: '16px',
      mt: '32px',
    },
  },
  logoutButton: {
    height: '44px',
    minWidth: '160px',
    mt: '32px',
    '@media screen and (max-width:600px)': {
      mt: '24px',
    },
  },
};

interface Props {
  /** 認証アプリの設定（バックアップコード保存まで）が完了したとき */
  onEnrolled: () => void;
}

/**
 * MFA テナント強制時の設定要求画面（#2984・PRD §5-3）。
 * 認証アプリ未設定のメンバーに対し、設定が完了するまで通常画面の代わりに表示する。
 * 設定ダイアログは閉じられない（disableClose）。別アカウントへの切替用にログアウト導線あり。
 */
const MfaSetupRequired: React.FC<Props> = ({ onEnrolled }) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(true);

  const handleLogout = async (): Promise<void> => {
    try {
      clearPatternListCache();
      await signOut(auth);
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[MfaSetupRequired] ログアウトエラー:', error);
    }
  };

  return (
    <>
      <MetaTitle customTitle="認証アプリの設定が必要です" />
      <Box sx={styles.container}>
        <Box sx={styles.content}>
          <Box component="img" src={logoImage} alt="Gen Drive" sx={styles.logo} />
          <Typography component="h1" sx={styles.message}>
            {MESSAGE}
          </Typography>
          <Button onClick={handleLogout} sx={[tertiaryButtonBackgroundColor, styles.logoutButton]}>
            ログアウト
          </Button>
        </Box>
      </Box>
      <MfaEnrollDialog
        open={dialogOpen}
        disableClose
        onClose={(): void => setDialogOpen(false)}
        onEnrolled={onEnrolled}
      />
    </>
  );
};

export default MfaSetupRequired;
